"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { toggleSaveJob } from "@/app/jobs/saved-actions";
import { capture, captureReliable } from "@/lib/analytics/events";
import type { SaveJobInput } from "@/lib/saved-jobs";

interface SavedState {
  authed: boolean;
  ids: number[];
}

/**
 * One GET per page load, shared by every button on it — memoised on the module
 * so a board with 50 rows still makes exactly one request. Deliberately not
 * cleared: a page load is the lifetime we want, and toggles update local state.
 */
let savedStatePromise: Promise<SavedState> | null = null;
function loadSavedState(): Promise<SavedState> {
  savedStatePromise ??= fetch("/api/saved", { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : { authed: false, ids: [] }))
    .catch(() => ({ authed: false, ids: [] }));
  return savedStatePromise;
}

/**
 * Bookmark toggle on a job row / job page. Optimistic. Signed-out visitors get
 * a "Save" that routes to sign-in (the hook into creating an account). Sits
 * above the row's stretched title link via z-10 so the click lands here.
 *
 * `initialSaved`/`isAuthed` are optional: a server-rendered (dynamic) page can
 * pass them and skip the round trip, while a cached page omits them and the
 * button resolves its own state after hydration. That optionality is what lets
 * the ~63K job detail pages be ISR instead of force-dynamic.
 */
export function SaveJobButton({
  job,
  initialSaved,
  isAuthed,
  compact = true,
  block = false,
  surface = "board",
}: {
  job: SaveJobInput;
  initialSaved?: boolean;
  isAuthed?: boolean;
  compact?: boolean;
  /** Full-width sidebar treatment, matching the Apply button's height. */
  block?: boolean;
  /** Where the button sits — the board, a job page, recommendations. */
  surface?: string;
}) {
  const selfResolve = isAuthed === undefined;
  const [saved, setSaved] = useState(initialSaved ?? false);
  const [authed, setAuthed] = useState(isAuthed ?? false);
  const [pending, setPending] = useState(false);
  // Send a signed-out click back to the exact page it came from (usually the
  // job itself), not a hardcoded board URL — otherwise the vacancy they
  // wanted to save is lost the moment they follow the magic link.
  const pathname = usePathname();

  useEffect(() => {
    if (!selfResolve) return;
    let live = true;
    loadSavedState().then((s) => {
      if (!live) return;
      setAuthed(s.authed);
      setSaved(s.ids.includes(job.jobId));
    });
    return () => {
      live = false;
    };
  }, [selfResolve, job.jobId]);

  const base = block
    ? "relative z-10 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-sm font-semibold transition-colors"
    : "relative z-10 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition-colors";

  if (!authed) {
    return (
      <Link
        href={`/auth/login?next=${encodeURIComponent(pathname || "/jobs")}`}
        rel="nofollow"
        // captureReliable (sendBeacon): plain `capture` here logged 0
        // save_job events in 30 days despite real /auth/login traffic —
        // same navigation-race loss already fixed on header/search-tabs
        // surfaces, applied here too (2026-09-09).
        onClick={() => captureReliable("login_cta_click", { surface: "save_job" })}
        title="Sign in to save this job to your pipeline"
        className={`${base} border-neutral-200 bg-white text-neutral-500 hover:border-neutral-400 hover:text-ink`}
      >
        <BookmarkIcon filled={false} />
        {block ? <span>Save this job</span> : compact ? null : <span>Save</span>}
      </Link>
    );
  }

  const toggle = async () => {
    if (pending) return;
    const prev = saved;
    setSaved(!prev);
    setPending(true);
    const res = await toggleSaveJob({ ...job, saved: prev });
    if (!res.ok) setSaved(prev);
    else {
      setSaved(res.saved);
      // Only the save direction. An unsave is not an engagement signal, and
      // counting both would make the number rise every time someone changes
      // their mind.
      if (res.saved) capture("job_save", { job_id: job.jobId, surface });
    }
    setPending(false);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={saved}
      title={saved ? "Saved — click to remove" : "Save this job"}
      className={`${base} ${
        saved
          ? "border-brand-300 bg-brand-50 text-brand-700 hover:border-brand-400"
          : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-400 hover:text-ink"
      } disabled:opacity-60`}
    >
      <BookmarkIcon filled={saved} />
      <span>{saved ? "Saved" : block ? "Save this job" : "Save"}</span>
    </button>
  );
}

function BookmarkIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden fill={filled ? "currentColor" : "none"}>
      <path
        d="M4 2.5h8a.5.5 0 0 1 .5.5v10.5l-4.5-2.6L3.5 13.5V3a.5.5 0 0 1 .5-.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
